import React, { useState } from 'react';
import {
    Chip,
    CircularProgress,
    Grid2,
    IconButton,
    Table,
    TableContainer,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import { useTranslation } from 'react-i18next';
import BlockWithTitle from '../../../components/atom/BlockWithTitle';
import ShowMoreToggle from './ShowMoreToggle';
import { formatDateTimeInVenueTz } from '../../../helpers/formatDateTime';
import { MODAL_TYPE, useModals } from '../../../hooks/useModal';

const PREVIEW_LIMIT = 5;

function BookingEmailLogsBlock({ emails, loading, reloadData, collapsible }) {
    const { t } = useTranslation();
    const { showModal } = useModals();
    const [showAll, setShowAll] = useState(false);

    if (loading) {
        return (
            <Grid2 size={{ xs: 12 }}>
                <BlockWithTitle title={t('emaillogs.title', 'Emails')} collapsible={collapsible}>
                    <Grid2 size={{ xs: 12 }} display="flex" justifyContent="center" p={2}>
                        <CircularProgress size={24} />
                    </Grid2>
                </BlockWithTitle>
            </Grid2>
        );
    }

    if (!emails || emails.length === 0) {
        return null;
    }

    const openEmail = (email) => {
        showModal(MODAL_TYPE.EMAIL_LOGS, {
            title: t('emaillogs.mail'),
            content: email,
            reloadData: () => reloadData(),
        });
    };

    return (
        <Grid2 size={{ xs: 12 }}>
            <BlockWithTitle title={t('emaillogs.title', 'Emails')} collapsible={collapsible}>
                <Grid2 size={{ xs: 12 }}>
                    <TableContainer>
                        <Table size="small" sx={{ minWidth: 650 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>{t('emaillogs.columns.subject')}</TableCell>
                                    <TableCell>{t('emaillogs.columns.result')}</TableCell>
                                    <TableCell>{t('emaillogs.columns.created')}</TableCell>
                                    <TableCell align="right" />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(showAll ? emails : emails.slice(0, PREVIEW_LIMIT)).map(
                                    (email) => {
                                        const isSuccess = email.result === 'success';
                                        return (
                                            <TableRow key={email.id} hover>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {email.subject}
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        color="textSecondary"
                                                    >
                                                        {email.to}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    {isSuccess ? (
                                                        <Chip
                                                            size="small"
                                                            color="success"
                                                            label={t(
                                                                'emaillogs.resultSuccess',
                                                                'Sent',
                                                            )}
                                                        />
                                                    ) : (
                                                        <Tooltip title={email.result || ''}>
                                                            <Chip
                                                                size="small"
                                                                color="error"
                                                                label={t(
                                                                    'emaillogs.resultFailed',
                                                                    'Failed',
                                                                )}
                                                            />
                                                        </Tooltip>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{ whiteSpace: 'nowrap' }}
                                                    >
                                                        {formatDateTimeInVenueTz(email.created_at)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Tooltip title={t('emaillogs.viewEmail')}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => openEmail(email)}
                                                        >
                                                            <RemoveRedEyeIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    },
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <ShowMoreToggle
                        total={emails.length}
                        limit={PREVIEW_LIMIT}
                        showAll={showAll}
                        onToggle={() => setShowAll((prev) => !prev)}
                    />
                </Grid2>
            </BlockWithTitle>
        </Grid2>
    );
}

export default BookingEmailLogsBlock;
